import { useState } from 'react';
import DeviceDropdown from '../dashboard/DeviceDropdown';
import IntervalButtons from '../dashboard/IntervalButtons';
import LoadingMessage from '../common/LoadingMessage';
import ErrorMessage from '../common/ErrorMessage';
import { useWanData } from '../../hooks/useWanData';
import { transformWanPayload } from '../../utils/wanTransform';
import ChartSection from './ChartSection';

function WanDashboard({ selectedDevice, onDeviceChange, selectedInterval, customRange, onIntervalChange, onRangeChange }) {
    const { wanData, loading, error, lastUpdated, refresh } = useWanData(selectedDevice, selectedInterval, customRange);
    const [yScaleType, setYScaleType] = useState('linear');
    const transformed = selectedDevice && !loading && !error && wanData ? transformWanPayload(wanData) : null;
    const showPlaceholder = !selectedDevice || loading;

    return (
        <div className="dashboard-content">
            <header style={{ marginBottom: '20px' }}>
                <h2>WAN Performance</h2>
                <p className="section-intro">
                    Inspect latency and packet-loss metrics across DNS, HTTP, and MTR sources. Select a device and time range to populate the charts; use refresh to pull the latest data.
                </p>
            </header>

            <section className="data-controls">
                <div className="data-controls__layout">
                    <div className="data-controls__device">
                        <div className="data-controls__title">Data analysis controls</div>
                        <DeviceDropdown selectedDevice={selectedDevice} onDeviceChange={onDeviceChange} />
                    </div>
                    <span className="data-controls__divider" aria-hidden="true" />
                    <div className="data-controls__interval">
                        <div className="data-controls__interval-label">Y-Axis Scale</div>
                        <div className="button-group">
                            <button 
                                type="button" 
                                className={`interval-btn ${yScaleType === 'linear' ? 'active' : ''}`}
                                onClick={() => setYScaleType('linear')}
                            >
                                Linear
                            </button>
                            <button 
                                type="button" 
                                className={`interval-btn ${yScaleType === 'log' ? 'active' : ''}`}
                                onClick={() => setYScaleType('log')}
                            >
                                Log
                            </button>
                        </div>
                    </div>
            <span className="data-controls__divider" aria-hidden="true" />
                    <div className="data-controls__interval">
                        <div className="data-controls__interval-label">Data interval</div>
                        <IntervalButtons
                            selectedInterval={selectedInterval}
                            onIntervalChange={onIntervalChange}
                            customRange={customRange}
                            onCustomRangeChange={onRangeChange}
                        />
                    </div>
                </div>
            </section>

            <div style={{ marginTop: '16px', display: 'flex', gap: '12px', alignItems: 'center' }}>
                <button
                    type="button"
                    onClick={refresh}
                    disabled={!selectedDevice || loading}
                    className="refresh-button"
                >
                    🔄 Refresh
                </button>
                {lastUpdated && <span className="refresh-meta">Last updated: {lastUpdated.toLocaleTimeString()}</span>}
                {!selectedDevice && !loading && !error && (
                    <span className="refresh-meta">Select a device to enable WAN charts.</span>
                )}
            </div>


            {error && (
                <div style={{ marginTop: '20px' }}>
                    <ErrorMessage message={error} title="Failed to load WAN data" />
                </div>
            )}

            {showPlaceholder && (
                <div style={{ marginTop: '30px' }}>
                    <LoadingMessage message={selectedDevice ? 'Loading WAN data…' : 'Select a device to load WAN data.'} />
                </div>
            )}

            {!loading && !error && selectedDevice && transformed && (
                <div style={{ marginTop: '24px' }}>
                    <ChartSection transformed={transformed} yScaleType={yScaleType} />
                </div>
            )}
        </div>
    );
}

export default WanDashboard;
